// src/components/LoginForm.jsx
import React, { useState } from "react";
import "../../styles/citemed-theme.css";
import logo from "../../assets/citemed_logo.png";

import { apiClient } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastProvider";

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FAFAFF',
    padding: '24px',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 25px -5px rgba(53, 120, 255, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    padding: '40px 32px',
    width: '100%',
    maxWidth: '420px',
    border: '1px solid var(--Gray-50)',
    position: 'relative',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoContainer: {
    marginBottom: '24px',
  },
  logo: {
    height: '48px',
    width: 'auto',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: 'var(--Gray-900)',
    marginBottom: '8px',
    fontFamily: 'Inter, sans-serif',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--Gray-600)',
    margin: 0,
  },
  description: {
    fontSize: '14px',
    color: 'var(--Gray-600)',
    lineHeight: '20px',
    margin: '16px 0 0 0',
    textAlign: 'center',
  },
  learnMoreContainer: {
    marginTop: '16px',
    textAlign: 'center',
  },
  learnMoreText: {
    fontSize: '13px',
    color: 'var(--Gray-600)',
  },
  learnMoreLink: {
    color: 'var(--Primary-600)',
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--Gray-700)',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 12px 10px 38px',
    fontSize: '14px',
    lineHeight: '20px',
    color: 'var(--Gray-900)',
    background: 'white',
    border: '1px solid var(--Gray-200)',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  },
  inputWrapper: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--Gray-400)',
    pointerEvents: 'none',
  },
  errorMessage: {
    marginTop: '12px',
    padding: '10px 12px',
    backgroundColor: 'var(--error-50)',
    border: '1px solid var(--error-100)',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorIcon: {
    flexShrink: 0,
    width: '16px',
    height: '16px',
    color: 'var(--Error-700)',
  },
  errorText: {
    fontSize: '13px',
    color: 'var(--Error-700)',
    lineHeight: '18px',
  },
  button: {
    width: '100%',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 600,
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    zIndex: 10,
  },
  footer: {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid var(--Gray-50)',
    textAlign: 'center',
  },
  helpText: {
    fontSize: '13px',
    color: 'var(--Gray-600)',
  },
  link: {
    color: 'var(--Primary-600)',
    fontWeight: 600,
    textDecoration: 'none',
  },
};

const LoginForm = () => {
  const { showToast } = useToast();
  const { setAuthData } = useAuth();
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Discover the correct environment and authenticate
      const baseURL = await apiClient.authenticateWithDiscovery(apiKey);
      apiClient.setBaseURL(baseURL);
      apiClient.setApiKey(apiKey);

      // Fetch user data from discovered environment
      const data = await apiClient.me();

      if (data) {
        setAuthData({ ...data, citationStyles: data["citation_styles"], apiKey });
      } else {
        setError("Invalid credentials");
      }
    } catch (err) {
      const errorMessage = err?.message || "Authentication failed";
      setError(errorMessage);

      // Show different toast messages based on error type
      if (errorMessage.includes("collision detected")) {
        showToast("API Key Collision", errorMessage, { intent: "error" });
      } else if (errorMessage.includes("Unable to reach")) {
        showToast("Network Error", errorMessage, { intent: "error" });
      } else if (errorMessage.includes("Invalid API key")) {
        showToast("Authentication Failed", errorMessage, { intent: "error" });
      } else {
        showToast("Auth Error", errorMessage, { intent: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <img src={logo} alt="CiteMed" style={styles.logo} />
          </div>
          <h1 style={styles.title}>Welcome back</h1>
          <p style={styles.description}>
            This add-in allows you to insert citations directly into Word in various formats (APA, MLA etc.) by connecting to your CiteSource reference library in Evidence Cloud. Write papers and cite references seamlessly.
          </p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <div style={styles.inputWrapper}>
              <svg style={styles.inputIcon} width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
              </svg>
              <input
                id="api-key"
                type="password"
                placeholder="Enter your API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                required
                disabled={loading}
                style={{
                  ...styles.input,
                  ...(loading && { opacity: 0.6, cursor: 'not-allowed' })
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--Primary-500)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(53, 120, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--Gray-200)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          
          {error && (
            <div style={styles.errorMessage}>
              <svg style={styles.errorIcon} viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M8 15A7 7 0 108 1a7 7 0 000 14zm0-1A6 6 0 108 2a6 6 0 000 12zm0-9a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 018 5zm0 7a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
              </svg>
              <span style={styles.errorText}>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !apiKey.trim()}
            style={styles.button}
          >
            {loading ? (
              <>
                <span className="spinner" style={{ width: '16px', height: '16px' }}></span>
                <span>Signing in...</span>
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div style={styles.learnMoreContainer}>
          <span style={styles.learnMoreText}>Not an Evidence Cloud Subscriber? </span>
          <a
            href="https://citemed.atlassian.net/wiki/external/ODA3OGUzMWI3MzBkNDAzM2ExNTY5YjcyODU1M2FjOWM"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.learnMoreLink}
          >
            Click Here to Learn More
          </a>
        </div>
        
        {loading && (
          <div style={styles.loadingOverlay}>
            <div style={{ textAlign: 'center' }}>
              <div className="spinner spinner-lg" style={{ marginBottom: '16px' }}></div>
              <p style={{ color: 'var(--Gray-600)', fontSize: '14px' }}>Authenticating...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;
